import React, { useEffect } from 'react';
import { Input, Modal, Form, Row, Col, Switch, Select, Button, message, InputNumber } from 'antd';
import { CloseOutlined, PaperClipOutlined, PhoneOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getShowAddNewLocationModal, showAddNewLocationModal, showLocationPicker } from '../../store/app-ui.store';
import {
  addLocation,
  getEditingLocation,
  getLocationCoords,
  // getPageStart,
  setEditingLocation,
  setLocationCoords,
  updateLocation,
} from '../../store/location.store';
import useGooglePlaces from '../../hooks/useGooglePlaces';

interface ILocation {
  locationName: string;
  address: string;
  status: 'ACTIVE' | 'INACTIVE';
  location_id?: string;
  isFactory?: boolean;
  sequence?: number;
  waiting_time?: number;
  contact_no: string;
}

const AddNewLocation = (): JSX.Element => {
  const { options, setQuery, getPlaceDetails } = useGooglePlaces();
  const addNewLocationModalVisibility = useAppSelector(getShowAddNewLocationModal);
  const editingLocation = useAppSelector(getEditingLocation);
  const dispatch = useAppDispatch();
  const coords = useAppSelector(getLocationCoords);

  const [form] = Form.useForm();
  const isEditing = !!editingLocation;

  useEffect(() => {
    if (addNewLocationModalVisibility && editingLocation) {
      form.setFieldsValue({
        locationName: editingLocation.locationName,
        address: editingLocation.address,
        waiting_time: editingLocation.waiting_time || 0,
        status: editingLocation.status === 'ACTIVE',
        contact_no: editingLocation.contact_no || '',
      });
      dispatch(
        setLocationCoords({
          lat: parseFloat(editingLocation.latitude || '0'),
          lng: parseFloat(editingLocation.longitude || '0'),
        }),
      );
    } else if (addNewLocationModalVisibility) {
      form.resetFields();
    }
  }, [addNewLocationModalVisibility, editingLocation, form, dispatch]);

  const handleSubmit = async (values: any) => {
    if (!coords) {
      message.error('Please select a valid address with coordinates');
      return;
    }
    const apiPayload = {
      locationName: values.locationName,
      address: values.address,
      longitude: coords.lng.toString(),
      latitude: coords.lat.toString(),
      status: values.status ? 'ACTIVE' : 'INACTIVE',
      waiting_time: values.waiting_time || 0,
      contact_no: values.contact_no || '',
    };

    let result;
    if (isEditing) {
      result = await dispatch(
        updateLocation({
          ...apiPayload,
          locationId: editingLocation!.location_id,
        }),
      );
    } else {
      result = await dispatch(addLocation(apiPayload));
    }

    if (result.type?.endsWith('/fulfilled')) {
      message.success(isEditing ? 'Location updated!' : 'Location added!');
      handleCancel();
    }
  };

  const handleCancel = () => {
    dispatch(showAddNewLocationModal(false));
    dispatch(setEditingLocation(null));
    dispatch(setLocationCoords(null));
    form.resetFields();
  };

  return (
    <Modal
      visible={addNewLocationModalVisibility}
      onOk={() => form.submit()}
      okText="OK"
      title={editingLocation ? 'Edit Location' : 'Add Location'}
      destroyOnClose
      onCancel={handleCancel}
      width={800}
    >
      <div style={{ width: '500px', margin: '0 auto' }}>
        <Form layout="vertical" form={form} name="login-form" onFinish={handleSubmit} initialValues={initValue}>
          <Form.Item label="Location Name: " name="locationName" rules={validations.name}>
            <Input placeholder="Location Name" prefix={<PaperClipOutlined />} />
          </Form.Item>
          <Form.Item label="Address:" name="address" rules={validations.address}>
            <Select
              showSearch
              placeholder="Address"
              onSearch={setQuery}
              options={options}
              filterOption={false}
              className="w-100"
              onSelect={async (v: any, o: any) => {
                const placeDetails = await getPlaceDetails(v?.toString() || '');
                if (placeDetails?.geometry) {
                  const lat = +String(placeDetails.geometry.location?.lat());
                  const lng = +String(placeDetails.geometry.location?.lng());
                  dispatch(setLocationCoords({ lat, lng }));
                  form.setFieldsValue({ address: o.label?.toString() });
                }
              }}
              allowClear
              clearIcon={<CloseOutlined />}
              onClear={() => {
                dispatch(setLocationCoords(null));
              }}
            />
          </Form.Item>
          <Form.Item label="Waiting Time (minutes):" name="waiting_time">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
          <Form.Item label="Contact Number:" name="contact_no" rules={validations.contact_no}>
            <Input placeholder="e.g. 94771234567" prefix={<PhoneOutlined />} />
          </Form.Item>
          <br />
          {coords ? (
            <>
              <p style={{ textAlign: 'center' }}>Lat : {coords.lat}</p>
              <p style={{ textAlign: 'center' }}>Lng : {coords.lng}</p>
              <Row justify="center">
                <Button key="add" type="primary" onClick={() => dispatch(showLocationPicker({ show: true }))}>
                  Edit Location
                </Button>
              </Row>
              <br />
            </>
          ) : null}
          <Row justify="center">
            <Col>
              <Row justify="center" align="middle">
                <span>Inactive</span>
                <Form.Item valuePropName="checked" name="status" style={{ margin: '0 10px' }}>
                  <Switch />
                </Form.Item>
                <span>Active</span>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

const initValue: ILocation = {
  locationName: '',
  address: '',
  status: 'ACTIVE',
  isFactory: false,
  sequence: 1,
  waiting_time: 0,
  contact_no: '',
};

const validations = {
  name: [{ required: true, message: 'Please enter location name' }],
  address: [{ required: true, message: 'Please enter location address' }],
  contact_no: [
    {
      pattern: /^94[0-9]{9}$/,
      message: 'Enter a valid number e.g. 94771234567',
    },
  ],
};

export default AddNewLocation;
